// featureStore.ts
import { Module } from 'vuex'
import { table } from '../enum/PhoneIntegration'
import isEmpty from "lodash-es/isEmpty"
import cloneDeep from "lodash-es/cloneDeep"
import {
  SubAccount,
  Notification,
  Migration,
  MigrationType,
  StoreFetchTrace,
  TwilioModalConfiguration,
  TwilioLoginModalType,
  TwilioForm,
  AgencyTwilio,
  PhoneNumber,
  TwilioAccountListRequest,
} from '../types/TwilioAccount'
import {
  TwilioAccountService,
  CompanyService,
  UserService,
  MoveNumbersService,
  requests,
} from '../services'
import { RootState } from '../../../store/state_models'
import config from '../../../config'

// Store state interface
export interface PhoneIntegrationStoreModule {
  loading: boolean
  agencyTwilioLinked: boolean
  agencyLCPhoneLinked: boolean
  totalSubAccounts: number
  tablePage: number
  searchQuery: string
  subAccounts: SubAccount[]
  subAccount: SubAccount
  phoneNumbers: Record<string, PhoneNumber>
  migration: Migration
  notification: Notification
  traceFetchReq: StoreFetchTrace
  agencyTwilio: AgencyTwilio
  twilioModalForm: TwilioForm
  twilioModal: TwilioModalConfiguration
  twilioV2ModalForm: TwilioForm
  twilioV2Modal: TwilioModalConfiguration
  stripe: any
}

export const phoneIntegration: Module<
  PhoneIntegrationStoreModule,
  RootState
> = {
  namespaced: true,
  state: {
    loading: false,
    agencyTwilioLinked: false,
    agencyLCPhoneLinked: false,
    totalSubAccounts: 0,
    tablePage: 1,
    searchQuery: '',
    subAccountsTableFilterCheckboxes: {
      isLcChecked: true,
      isTwilioChecked: true,
      isNoneChecked: true,
      isPhoneRebillingYesChecked: true,
      isPhoneRebillingNoChecked: true,
      isNumberIntelligenceEnabledChecked: true,
      isNumberIntelligenceDisabledChecked: true,
      isUnderMessagingRampYesChecked: true,
      isUnderMessagingRampNoChecked: true,
      isHasPhoneNumbersYesChecked: true,
      isHasPhoneNumbersNoChecked: true,
      isA2PVerifiedYesChecked: true,
      isA2PVerifiedNoChecked: true,
      isSmsRestrictedYesChecked: true,
      isSmsRestrictedNoChecked: true,
    },
    subAccounts: [],
    subAccount: {},
    phoneNumbers: {},
    migration: {
      type: '',
      modal: false,
    },
    notification: {
      visible: false,
      title: '',
      description: '',
      content: '',
      meta: '',
      duration: 0,
      action: '',
    },
    traceFetchReq: {
      subAccounts: '',
    },
    twilioModalForm: {
      accountSid: '',
      authToken: '',
      autoSubAccounts: false,
    },
    twilioModal: {
      context: '' as TwilioLoginModalType,
      showModal: false,
      cancelCallback: undefined,
      successCallback: undefined,
      failureCallback: undefined,
    },
    twilioV2ModalForm: {
      accountSid: '',
      authToken: '',
      autoSubAccounts: false,
    },
    twilioV2Modal: {
      context: '' as TwilioLoginModalType,
      showModal: false,
      cancelCallback: undefined,
      successCallback: undefined,
      failureCallback: undefined,
    },
    agencyTwilio: {
      id: '',
      accountSid: '',
      companyId: '',
      traceId: '',
    },
    stripe: {},
  } as PhoneIntegrationStoreModule,

  getters: {
    agencyLinkageStatus: state => ({
      twilio: state.agencyTwilioLinked,
      LCPhone: state.agencyLCPhoneLinked,
    }),

    notificationDetail: state => state.notification,
  },

  mutations: {
    linkAgencyTwilio(state) {
      state.agencyTwilioLinked = true
    },

    linkAgencyLCPhone(state) {
      state.agencyLCPhoneLinked = true
      state.subAccounts = []
    },

    unlinkAgencyTwilio(state) {
      state.agencyTwilioLinked = false
    },

    unlinkAgencyLCPhone(state) {
      state.agencyLCPhoneLinked = false
      state.subAccounts = []
    },

    notify(state, data: Notification) {
      const temp = { ...state.notification, ...data }
      state.notification = temp
      state.notification.visible = true
    },

    setTotalSubAccounts(state, count: number) {
      state.totalSubAccounts = count
    },

    setSubAccounts(state, list: SubAccount[]) {
      state.subAccounts = list
    },

    setSubAccount(state, subAccount: SubAccount) {
      state.subAccount = subAccount
    },

    setPhoneNumber(state, data) {
      const { phoneNumberSID, phoneNumber } = data
      state.phoneNumbers[phoneNumberSID] = phoneNumber
    },

    setSearchQuery(state, query: string) {
      state.searchQuery = query
    },

    setSubAccountsTableFilters(state, filters: JSON) {
      state.tablePage = 1
      state.subAccountsTableFilterCheckboxes = filters;
    },

    setAgencyTwilio(state, payload) {
      state.agencyTwilio = payload
    },

    setTwilioModalForm(state, payload) {
      state.twilioModalForm = {
        accountSid: payload.accountSid,
        autoSubAccounts: payload.autoSubAccounts,
        authToken: payload.authToken,
      }
    },

    setTwilioModal(state, payload) {
      const {
        context,
        cancelCallback,
        successCallback,
        failureCallback,
      } = payload

      state.twilioModal.context = context
      state.twilioModal.cancelCallback = cancelCallback
      state.twilioModal.successCallback = successCallback
      state.twilioModal.failureCallback = failureCallback
      state.twilioModal.showModal = true
    },

    setTablePage(state, page: number) {
      state.tablePage = page
    },

    setStripe(state, payload: any) {
      state.stripe = payload
    },

    startLoading(state) {
      state.loading = true
    },

    stopLoading(state) {
      state.loading = false
    },

    traceFetchReqSubAccounts(state, payload: string) {
      state.traceFetchReq.subAccounts = payload
    },

    resetTablePage(state) {
      // might be obsolete
      state.tablePage = 1
    },

    clearSubAccount(state) {
      // might be obsolete
      state.subAccount = {}
    },

    clearSubAccounts(state) {
      state.subAccounts = []
    },

    clearPhoneNumbers(state) {
      // might be obsolete
      state.phoneNumbers = {}
    },

    clearNotification(state) {
      state.notification = {
        visible: false,
        title: '',
        description: '',
        content: '',
        meta: '',
        duration: 0,
        action: '',
      }
    },

    clearAgencyTwilio(state) {
      state.agencyTwilio = {
        id: '',
        accountSid: '',
        companyId: '',
        traceId: '',
      }
    },

    clearTwilioModalForm(state) {
      state.twilioModalForm = {
        accountSid: '',
        authToken: '',
        autoSubAccounts: false,
      }
    },

    openModalMigration(state, type: MigrationType) {
      state.migration.modal = true
      state.migration.type = type
    },

    closeModalMigration(state) {
      state.migration.modal = false
      state.migration.type = ''
    },

    resetTwilioModal(state) {
      // might be obsolete
      state.twilioModal = {
        context: '' as TwilioLoginModalType,
        showModal: false,
        cancelCallback: undefined,
        successCallback: undefined,
        failureCallback: undefined,
      }
    },

    setTwilioV2ModalForm(state, payload) {
      state.twilioV2ModalForm = {
        accountSid: payload.accountSid || '',
        authToken: payload.authToken || '',
        autoSubAccounts: payload.autoSubAccounts || false,
      }
    },

    setTwilioV2Modal(state, payload) {
      const {
        context,
        cancelCallback,
        successCallback,
        failureCallback,
      } = payload

      state.twilioV2Modal.context = context
      state.twilioV2Modal.cancelCallback = cancelCallback
      state.twilioV2Modal.successCallback = successCallback
      state.twilioV2Modal.failureCallback = failureCallback
      state.twilioV2Modal.showModal = true
    },

    clearTwilioV2ModalForm(state) {
      state.twilioV2ModalForm = {
        accountSid: '',
        authToken: '',
        autoSubAccounts: false,
      }
    },

    resetTwilioV2Modal(state) {
      state.twilioV2Modal = {
        context: '' as TwilioLoginModalType,
        showModal: false,
        cancelCallback: undefined,
        successCallback: undefined,
        failureCallback: undefined,
      }
    },
  },

  actions: {
    setAgencyTwilio({ commit }, data) {
      commit('setAgencyTwilio', data)
      commit(data.accountSid ? 'linkAgencyTwilio' : 'unlinkAgencyTwilio')
    },

    openTwilioModal(
      { commit },
      data: TwilioModalConfiguration & {
        twilioModalForm: TwilioForm
      }
    ) {
      const {
        context,
        cancelCallback,
        successCallback,
        failureCallback,
        twilioModalForm,
      } = data
      commit('setTwilioModalForm', twilioModalForm)
      commit('setTwilioModal', {
        context,
        cancelCallback,
        successCallback,
        failureCallback,
      })
    },

    closeTwilioModal({ commit }) {
      commit('clearTwilioModalForm')
      commit('resetTwilioModal')
    },


    openTwilioV2Modal(
      { commit, state },
      data: TwilioModalConfiguration & {
        twilioModalForm: TwilioForm
      }
    ) {
      const {
        context,
        cancelCallback,
        successCallback,
        failureCallback,
        twilioModalForm,
      } = data
      commit('setTwilioV2ModalForm', twilioModalForm)
      commit('setTwilioV2Modal', {
        context,
        cancelCallback,
        successCallback,
        failureCallback,
      })
    },

    closeTwilioModalV2({ commit }) {
      commit('clearTwilioV2ModalForm')
      commit('resetTwilioV2Modal')
    },

    updateListSubAccountTwilio({ commit, state }, twilioAccountData) {
      const list = cloneDeep(state.subAccounts)
      const index = list.findIndex(
        item => item.id === twilioAccountData.locationId
      )

      list[index]['twilioAccount'] = twilioAccountData

      commit('clearSubAccounts')
      commit('setSubAccounts', list)
    },

    updateListSubAccount({ commit, state }, data) {
      const { updatedSubAccount, updateStoreSubAccount = false } = data
      const list = cloneDeep(state.subAccounts)
      const index = list.findIndex(item => item.id === updatedSubAccount.id)

      if (index >= 0) {
        list[index] = updatedSubAccount
        commit('clearSubAccounts')
        commit('setSubAccounts', list)
      }

      if (updateStoreSubAccount) {
        commit('setSubAccount', list[index])
      }
    },

    async fetchSubAccounts({ commit, state, rootState }, pageIndex) {
      commit('startLoading')
      commit('setTablePage', pageIndex ?? state.tablePage ?? 1)
      commit('clearSubAccounts')

      const { company } = rootState?.company as any
      const { id } = company

      try {
        const payload: TwilioAccountListRequest = {
          companyId: id,
          query: state.searchQuery,
          deleted: false,
          limit: table.SIZE,
          skip: state.tablePage > 1 ? table.OFFSET * (state.tablePage - 1) : 0,

          source: 'PHONE_SUB_ACCOUNT_SETTINGS_PAGE',
          subAccountsTableFilterCheckboxes: state.subAccountsTableFilterCheckboxes,
        }

        const res = await TwilioAccountService.list(payload)
        if (res && !isEmpty(res.hit)) {
          if (res && res.locations) {
            const subAccounts = res.locations.map(location => ({
              ...location,
              company: company,
            }))
            commit('setSubAccounts', subAccounts)
            commit('setTotalSubAccounts', res.hit[0].count)
            commit('traceFetchReqSubAccounts', res.traceId)
          }
        }

        commit('stopLoading')
        return Promise.resolve(res)
      } catch (error) {
        commit('stopLoading')
        return Promise.resolve(error)
      }
    },

    async exportDetails(_, companyId) {
      try{
        const res = await CompanyService.exportDetails(companyId)
        return Promise.resolve(res)
      } catch (error) {
        return Promise.resolve(error)
      }
    },

    async fetchTotalSubAccounts({ rootState }, query) {
      const { company } = rootState?.company as any
      const { id } = company

      try {
        let subAccounts
        const payload = {
          companyId: id,
          query: query,
          deleted: false,
        }
        const res = await TwilioAccountService.list(payload)
        if (res && !isEmpty(res.hit)) {
          if (res && res.locations) {
            subAccounts = res.locations.map(location => ({
              ...location,
              company: company,
            }))
          }
        }
        return Promise.resolve(subAccounts)
      } catch (error) {
        return Promise.resolve(error)
      }
    },

    async fetchAgency({ commit, rootState }) {
      const { company } = rootState?.company as any
      const { id } = company

      try {
        const { data } = await CompanyService.getById(id)

        commit(data.company.inIsvMode ? 'linkAgencyLCPhone' : 'unlinkAgencyLCPhone')

        return Promise.resolve(data.company)
      } catch (error: any) {
        commit('unlinkAgencyLCPhone')
        return Promise.reject(error)
      }
    },

    async fetchAgencyTwilio({ commit, dispatch, rootState }) {
      const { company } = rootState?.company as any
      const { id } = company
      try {
        const res = await TwilioAccountService.getAgencyTwilio(id)
        dispatch('setAgencyTwilio', res)

        return Promise.resolve(res)
      } catch (error: any) {
        commit('clearAgencyTwilio')
        return Promise.reject(error)
      }
    },

    async fetchMigrationCount({ commit }, payload) {
      try {
        const res = await TwilioAccountService.getMigrationCount(payload)
        return res
      } catch (error) {
        throw error
      }
    },

    async fetchSuspendedSubAccounts({ commit }, payload) {
      try {
        const res = await TwilioAccountService.getSuspendedSubAccounts(payload)
        return res
      } catch (error) {
        throw error
      }
    },

    async validateSubAccountSMS({ commit }, { payload, locationId }) {
      commit('startLoading')

      try {
        const res = await TwilioAccountService.validateSubAccountSMS(
          payload,
          locationId
        )

        commit('stopLoading')
        return Promise.resolve(res)
      } catch (error: any) {
        commit('stopLoading')
        return Promise.reject(error)
      }
    },

    async createSubAccount(_, locationId) {
      try {
        const res = await TwilioAccountService.createSubAccount(locationId)

        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },

    async fetchUser(_, userId) {
      try {
        const res = await UserService.getUserById(userId)

        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async enableBulkISV(_, { user, companyId }) {
      try {
        const res = TwilioAccountService.bulkEnableISVModeForLocation({
          user,
          companyId,
        })

        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async enableSingleISV(_, { user, locationId }) {
      try {
        const res = TwilioAccountService.enableISVModeForLocation({
          user,
          locationId,
        })

        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async enableAgencyISV(_, payload) {
      const { companyId, stripe, purchaseDescription } = payload
      try {
        const res = TwilioAccountService.enableISVModeForCompany(companyId, stripe, purchaseDescription)

        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async removeTwilioAccount(
      _,
      { companyId, locationId, closeSubAccounts }
    ) {
      try {
        const res = await TwilioAccountService.delete(
          companyId,
          locationId,
          closeSubAccounts
        )

        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async removeIsvSubAccount(
      _,
      {locationId, companyId, deletePhoneNumbers, deleteCampaigns, closeSubAccounts}
    ) {
      try {
        const res = await TwilioAccountService.deleteIsvSubAccount(
          locationId,
          companyId,
          deletePhoneNumbers,
          deleteCampaigns,
          closeSubAccounts
        )

        return res
      } catch (error) {
        throw error
      }
    },
    async updateAgencyTwilio(_, { payload, companyId }) {
      try {
        const check = await TwilioAccountService.checkAccountToken({
          accountSid: payload.accountSID,
          accountToken: payload.accountToken,
          companyId
        })
        if (check?.data === 'invalid')
          return Promise.reject({ message: 'Invalid Auth token' })

        const res = await TwilioAccountService.updateAgencyTwilio(
          payload,
          companyId
        )

        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async updateSubAccountTwilio(_, { payload, locationId }) {
      try {
        const res = await TwilioAccountService.updateSubAccountTwilio(
          payload,
          locationId
        )
        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error?.response?.data ?? error)
      }
    },
    async fetchInboundNumbers(_, payload) {
      try {
        const res = await MoveNumbersService.getInboundNumbers(payload)

        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },
    async moveNumbers(_, payload) {
      try {
        const res = await MoveNumbersService.moveNumbers(payload)

        return Promise.resolve(res)
      } catch (error) {
        return Promise.reject(error)
      }
    },

    async checkRebillingPossibility({ commit, rootState }, { subAccountId }) {
      const { company } = rootState?.company as any
      const { id: companyId } = company

      try {
        commit('startLoading')
        const res = await TwilioAccountService.checkRebillingPossibility(subAccountId, companyId)
        commit('stopLoading')
        return Promise.resolve(res)
      } catch (error) {
        commit('stopLoading')
        return Promise.reject(error)
      }
    },

    async updateRebillingConfig({ commit, rootState }, payload) {
      const { company } = rootState?.company as any
      const { id: companyId } = company

      if (!payload.rebillingConfig.companyId) {
        payload.rebillingConfig.companyId = companyId
      }

      try {
        commit('startLoading')
        const res = await TwilioAccountService.updateRebillingConfig(payload)
        commit('stopLoading')
        return Promise.resolve(res)
      } catch (error) {
        commit('stopLoading')
        return Promise.reject(error)
      }
    },
    async getMigratedNumbers({ commit, rootState }, payload) {
      try {
        const { locationId } = payload
        const res = await TwilioAccountService.getMigratedNumbers(locationId)
        return res
      } catch (error) {
        throw error
      }
    },
    async fetchSmsComplianceConfig({ commit, rootState }, payload) {
      try {
        const res = await TwilioAccountService.getSmsComplianceConfig(payload)
        return res
      } catch (error) {
        throw error
      }
    },
    async updateSmsComplianceConfig({ commit, rootState }, payload) {
      try {
        const res = await TwilioAccountService.updateSmsComplianceConfig(payload)
        return res
      } catch (error) {
        throw error
      }
    }
  },
}
